import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { resolverRol } from '../components/resolverRol';
import { normalizarRolVisitas } from '../lib/roles';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

const APP = 'visitas';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [rawVisitsRole, setVisitsRole] = useState(null)
  const [crmRole, setCrmRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  // true cuando la consulta de la membresía FALLÓ y resolverRol agotó su
  // reintento, no cuando contestó que la persona no es miembro. Señal aparte
  // para que el guardián no confunda "no sabemos" con "no tiene acceso".
  const [rolError, setRolError] = useState(false)
  // Id del usuario para el que perfil y rol ya están (o están por quedar)
  // resueltos. Va en un ref, no en el state `user`: onAuthStateChange se
  // suscribe una sola vez dentro del useEffect de deps [], así que su closure
  // quedaría atada para siempre al `user` del primer render (null).
  const resolvedUserIdRef = useRef(null)
  // Sube en cada resolucion del rol. Cada llamada se queda con su numero y,
  // al volver del await, si el contador ya avanzo es que hay otra mas nueva:
  // descarta lo suyo. Sin esto, dos resoluciones cruzadas dejan mandando a la
  // que contesta ultima, que puede ser la vieja.
  const peticionRolRef = useRef(0);

  const profileOperations = {
    async load(userId) {
      if (!userId) return
      const mia = ++peticionRolRef.current;
      setProfileLoading(true)
      try {
        // user_profiles in vlsdb no longer carries role — role lives in app_memberships.
        // crm membership: para el link "Abrir en CRM" (solo quien tiene acceso lo ve).
        // La membresía pasa por resolverRol y el resto no, a propósito: la
        // membresía decide si la persona entra, lo demás es dato de adorno. Antes
        // un `mErr` solo se logueaba con un warn y dejaba el rol en null, o sea
        // que una consulta que fallaba se leía igual que "no es miembro" y el
        // guardián mostraba "sin acceso".
        // La de CRM tampoco: solo decide si se ve el link "Abrir en CRM".
        const [{ data: profile, error: pErr }, membership, { data: crmMembership }] = await Promise.all([
          supabase?.from('user_profiles')?.select('id, email, full_name, avatar_url, active')?.eq('id', userId)?.single(),
          resolverRol(async () => {
            const { data, error } = await supabase?.from('app_memberships')?.select('role')?.eq('user_id', userId)?.eq('app', APP)?.maybeSingle()
            return { data: data?.role ?? null, error }
          }),
          supabase?.from('app_memberships')?.select('role')?.eq('user_id', userId)?.eq('app', 'crm')?.maybeSingle(),
        ])
        if (mia !== peticionRolRef.current) return; // llego tarde
        if (!pErr && profile) setUserProfile(profile)
        setRolError(membership?.estado === 'error')
        setVisitsRole(membership?.rol ?? null)
        setCrmRole(crmMembership?.role ?? null)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        // Solo la resolucion vigente apaga el "cargando". Si esta llamada ya
        // quedo vieja, apagarlo destaparia la app con el rol sin actualizar.
        if (mia === peticionRolRef.current) setProfileLoading(false)
      }
    },
    clear() {
      setUserProfile(null)
      setVisitsRole(null)
      setCrmRole(null)
      setRolError(false)
      setProfileLoading(false)
    }
  }

  // Reintento manual desde el botón de la pantalla de error. Solo tiene sentido
  // con sesión activa: es el único caso en que rolError puede quedar en true.
  const reintentarRol = () => {
    if (!resolvedUserIdRef.current) return
    setRolError(false)
    profileOperations.load(resolvedUserIdRef.current)
  }

  const authStateHandlers = {
    // Escribe el ref acá, en el único lugar que resuelve, para que la guarda de
    // la suscripción tenga contra qué comparar.
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      resolvedUserIdRef.current = session?.user?.id ?? null
      if (session?.user) {
        profileOperations?.load(session?.user?.id)
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (error?.message?.includes('Refresh Token Not Found') || error?.message?.includes('Invalid Refresh Token')) {
        supabase?.auth?.signOut()
        setUser(null)
        profileOperations?.clear()
        setLoading(false)
        return
      }
      authStateHandlers?.onChange(null, session)
    })
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        if (event === 'TOKEN_REFRESHED' && !session) {
          supabase?.auth?.signOut()
          resolvedUserIdRef.current = null
          setUser(null)
          profileOperations?.clear()
          setLoading(false)
          return
        }
        // Misma persona que la ya resuelta (ej. TOKEN_REFRESHED CON sesión, que
        // Supabase dispara cada hora con autoRefreshToken:true, y los re-emits
        // al volver el foco a la pestaña): no hay nada que re-resolver. Sin esta
        // guarda, cada refresh volvía a llamar a load(), que arranca con
        // setProfileLoading(true), y el guardián desmontaba y remontaba TODA la
        // app para mostrar el overlay de carga, perdiendo lo que hubiera sin
        // guardar en cualquier formulario abierto.
        if ((session?.user?.id ?? null) === resolvedUserIdRef.current) {
          setUser(session?.user ?? null)
          setLoading(false)
          return
        }
        authStateHandlers?.onChange(event, session)
      }
    )
    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Error de red. Inténtalo de nuevo.' } }
    }
  }

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
          emailRedirectTo: `${window.location.origin}/login`,
        },
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Error de red. Inténtalo de nuevo.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        // El ref se limpia junto con el state: si quedara con el id viejo y la
        // misma persona volviera a entrar, el SIGNED_IN se leería como "mismo
        // usuario ya resuelto" y se quedaría sin perfil ni rol.
        resolvedUserIdRef.current = null
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Error de red. Inténtalo de nuevo.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No hay usuario autenticado' } }
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Error de red. Inténtalo de nuevo.' } }
    }
  }

  // Role helpers derived from visits membership
  const visitsRole = normalizarRolVisitas(rawVisitsRole)
  const isAdmin = visitsRole === 'admin'
  const isManager = isAdmin // alias de compatibilidad (manager ahora es admin)
  const isEditor = visitsRole === 'editor'
  const isFieldRep = isEditor // alias de compatibilidad (field_rep ahora es editor)
  const hasVisitsAccess = visitsRole !== null
  const hasCrmAccess = crmRole !== null
  const userDisplayName = userProfile?.full_name || user?.email?.split('@')?.[0] || 'Usuario'
  const userRole = visitsRole

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    isAdmin,
    isManager,
    isEditor,
    isFieldRep,
    hasVisitsAccess,
    hasCrmAccess,
    userDisplayName,
    userRole,
    rolError,
    reintentarRol,
    visitsRole,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
